import type { WorkflowDefinitionDocument } from './platform-workflow-definition-lifecycle.models';

export interface RouteDestinationPresentation {
  state:
    | 'step'
    | 'completion'
    | 'unselected'
    | 'missing'
    | 'ambiguous'
    | 'unavailable';
  label: string;
  typeLabel: string;
  sentence: string;
  requiresPathInspection: boolean;
}

/**
 * Describes a selected destination; it never infers movement from display order.
 * The editor supplies its eligible choices when it also needs movement validity.
 */
export function routeDestinationPresentation(
  document: WorkflowDefinitionDocument,
  sourceNodeId: string,
  destinationNodeId: string,
  movement: 'forward' | 'return',
  eligibleDestinationIds?: readonly string[],
): RouteDestinationPresentation {
  if (typeof destinationNodeId !== 'string' || !destinationNodeId.trim())
    return {
      state: 'unselected',
      label: 'Choose a destination',
      typeLabel: 'No destination',
      sentence:
        movement === 'return'
          ? 'Choose the work step that receives this return visit.'
          : 'Choose a work step or Complete workflow for this path.',
      requiresPathInspection: false,
    };

  const matches = document.nodes.filter(
    (node) => node?.id === destinationNodeId,
  );
  if (!matches.length)
    return {
      state: 'missing',
      label: `Missing destination (${destinationNodeId})`,
      typeLabel: 'Missing destination',
      sentence:
        'This path points to a destination that is no longer in the workflow. Choose another destination or inspect workflow paths.',
      requiresPathInspection: true,
    };
  if (matches.length !== 1)
    return {
      state: 'ambiguous',
      label: `Ambiguous destination (${destinationNodeId})`,
      typeLabel: 'Ambiguous destination',
      sentence:
        'More than one node uses this destination identity. Inspect workflow paths before changing it.',
      requiresPathInspection: true,
    };

  const destination = matches[0];
  const name =
    destination.displayName?.trim() ||
    destination.stepCode?.trim() ||
    destination.id;
  const label =
    destination.typeCode === 'terminal'
      ? /^(complete|complete workflow)$/i.test(name)
        ? 'Complete workflow'
        : `Complete workflow · ${name}`
      : name;
  const unavailable = (sentence: string): RouteDestinationPresentation => ({
    state: 'unavailable',
    label,
    typeLabel: 'Unavailable destination',
    sentence,
    requiresPathInspection: true,
  });
  const sources = document.nodes.filter((node) => node?.id === sourceNodeId);
  if (
    typeof sourceNodeId !== 'string' ||
    !sourceNodeId.trim() ||
    sources.length !== 1
  )
    return unavailable(
      'The source work step is missing or ambiguous. Inspect workflow paths before changing this destination.',
    );
  if (sources[0].typeCode !== 'human_step')
    return unavailable(
      'This source is not a work step. Inspect workflow paths before changing this destination.',
    );
  if (!['human_step', 'terminal'].includes(destination.typeCode))
    return unavailable(
      'This destination uses routing that must be inspected in workflow paths.',
    );
  if (destinationNodeId === sourceNodeId)
    return unavailable(
      'This path points to the same work step. Choose another destination or inspect workflow paths.',
    );
  if (movement === 'return' && destination.typeCode === 'terminal')
    return unavailable(
      'A return visit must lead to a work step. Complete workflow is not available for returns.',
    );
  if (
    eligibleDestinationIds !== undefined &&
    !eligibleDestinationIds.includes(destinationNodeId)
  )
    return unavailable(
      `“${label}” is not available for a ${movement === 'return' ? 'return visit' : 'forward path'} from this step. Choose an available destination or inspect workflow paths.`,
    );
  if (movement === 'return')
    return {
      state: 'step',
      label,
      typeLabel: 'Return visit',
      sentence: `Return to “${label}” for a fresh visit. At runtime, the destination must already have a completed or bypassed visit in this workflow. Required evidence is collected again for that visit; previous visit history is retained.`,
      requiresPathInspection: false,
    };
  if (destination.typeCode === 'terminal')
    return {
      state: 'completion',
      label,
      typeLabel: 'Workflow completion',
      sentence:
        'After this step completes, this path leads to workflow completion. Workflow completion checks still apply.',
      requiresPathInspection: false,
    };
  return {
    state: 'step',
    label,
    typeLabel: 'Work step',
    sentence: `When this step completes, continue to “${label}”.`,
    requiresPathInspection: false,
  };
}

export interface DirectRoutePresentation {
  state: 'unconfigured' | 'direct' | 'inspect';
  label: string;
  sentence: string;
  destination: RouteDestinationPresentation | null;
}

/** Summarizes only a single direct path, without resolving conditions or outcomes. */
export function directRoutePresentation(
  document: WorkflowDefinitionDocument,
  sourceNodeId: string,
  eligibleDestinationIds?: readonly string[],
): DirectRoutePresentation {
  const sources = document.nodes.filter((node) => node?.id === sourceNodeId);
  if (
    typeof sourceNodeId !== 'string' ||
    !sourceNodeId.trim() ||
    sources.length !== 1 ||
    sources[0].typeCode !== 'human_step'
  )
    return {
      state: 'inspect',
      label: 'Work step unavailable',
      sentence:
        'A single available source work step is required. Inspect workflow paths.',
      destination: null,
    };
  const paths = document.edges.filter(
    (edge) => edge?.fromNodeId === sourceNodeId,
  );
  if (!paths.length)
    return {
      state: 'unconfigured',
      label: 'Choose a destination',
      sentence:
        'No outgoing path is configured. Choose what happens after this step completes.',
      destination: null,
    };
  if (paths.length !== 1)
    return {
      state: 'inspect',
      label: `${paths.length} outgoing paths`,
      sentence:
        'Inspect workflow paths to see which destination applies. This step does not have a single direct destination.',
      destination: null,
    };
  const path = paths[0];
  if (path.outcomeCode != null || path.pathKindCode != null)
    return {
      state: 'inspect',
      label:
        path.pathKindCode === 'return'
          ? 'Return path'
          : path.outcomeCode != null
            ? 'Outcome routing'
            : 'Routing needs inspection',
      sentence:
        'Inspect workflow paths to see how this path is selected and where it leads.',
      destination: null,
    };
  const destination = routeDestinationPresentation(
    document,
    sourceNodeId,
    path.toNodeId,
    'forward',
    eligibleDestinationIds,
  );
  return {
    state:
      destination.state === 'step' || destination.state === 'completion'
        ? 'direct'
        : 'inspect',
    label: destination.label,
    sentence:
      destination.state === 'unselected'
        ? 'This path has no destination. Inspect workflow paths to repair it.'
        : destination.sentence,
    destination,
  };
}
